import { useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, MoreHorizontal, Pencil, Pill, Plus, Power, PowerOff } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  getCatalogoFarmacosControllerListarQueryKey,
  useCatalogoFarmacosControllerAtualizar,
  useCatalogoFarmacosControllerClassesTerapeuticas,
  useCatalogoFarmacosControllerDesativar,
  useCatalogoFarmacosControllerListar,
  useCatalogoFarmacosControllerSugestoes,
} from '@/api/generated/admin-catalogo-farmacos/admin-catalogo-farmacos';
import type { CatalogoFarmacoResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { FarmacoFormDialogo } from '@/features/admin/catalogo-farmacos/components/FarmacoFormDialogo';
import { FundirFarmacoDialogo } from '@/features/admin/catalogo-farmacos/components/FundirFarmacoDialogo';
import { SugestoesDoClinico } from '@/features/admin/catalogo-farmacos/components/SugestoesDoClinico';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { rotaDoFarmacoDoCatalogo } from '@/shared/lib/rotas';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const TODOS = 'todos';

/**
 * As duas abas.
 *
 * ⚠️ Aba, e não uma tela à parte com rota própria. A fila de sugestões e o
 * catálogo são a MESMA tabela por dois recortes de `origem`, e o trabalho passa
 * de um lado para o outro o tempo todo — fundir uma sugestão mexe num item
 * curado, completar uma sugestão a transforma num. Duas telas separadas fariam o
 * admin navegar de ida e volta a cada item.
 */
type Aba = 'catalogo' | 'sugestoes';

/**
 * Catálogo de fármacos — administração (B14-1).
 *
 * ⚠️ Catálogo GLOBAL: o que se edita aqui vale para todas as clínicas, e nada
 * é apagado. Um fármaco descontinuado continua sendo o que aquele animal tomou
 * — por isso "excluir" é desativar, e duplicata se resolve com fusão.
 */
export function CatalogoFarmacosPage() {
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();

  const [aba, setAba] = useState<Aba>('catalogo');
  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [classe, setClasse] = useState<string>(TODOS);
  const [situacao, setSituacao] = useState<string>(TODOS);
  const [formAberto, setFormAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<CatalogoFarmacoResponse | undefined>();
  const [aFundir, setAFundir] = useState<CatalogoFarmacoResponse | null>(null);
  const [aDesativar, setADesativar] = useState<CatalogoFarmacoResponse | null>(null);

  // A busca do servidor atravessa princípio ativo, sinônimos e nome comercial
  // ("Vetoryl" acha "Trilostano") e roda em memória lá — uma requisição por
  // tecla seria varredura do catálogo inteiro a cada letra.
  const buscaAdiada = useDebounce(busca);

  const { data, isPending, isError, refetch } = useCatalogoFarmacosControllerListar({
    pagina,
    porPagina: 20,
    ordenarPor: 'nome',
    ordem: 'asc',
    ...(buscaAdiada.trim() ? { busca: buscaAdiada.trim() } : {}),
    ...(classe !== TODOS ? { classeTerapeutica: classe } : {}),
    ...(situacao !== TODOS ? { ativo: situacao as 'true' | 'false' } : {}),
  });

  const { data: classes } = useCatalogoFarmacosControllerClassesTerapeuticas();

  /*
   * ⚠️ A contagem é consultada MESMO na aba do catálogo, e é de propósito: uma
   * fila que só aparece depois de clicada é uma fila que ninguém abre. O número
   * na aba é o que faz o admin descobrir que há trabalho esperando — sem ele, o
   * catálogo encheria de "trilostano/Trilostano/trilostan" com a tela em ordem.
   */
  const sugestoes = useCatalogoFarmacosControllerSugestoes();
  const totalDeSugestoes = sugestoes.data?.total ?? 0;
  const desativar = useCatalogoFarmacosControllerDesativar();
  const atualizar = useCatalogoFarmacosControllerAtualizar();

  const podeCriar = pode('admin_catalogo_farmacos.criar');
  const podeEditar = pode('admin_catalogo_farmacos.editar');
  const podeExcluir = pode('admin_catalogo_farmacos.excluir');

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  async function recarregar() {
    await queryClient.invalidateQueries({
      queryKey: getCatalogoFarmacosControllerListarQueryKey(),
    });
  }

  async function confirmarDesativacao() {
    if (!aDesativar) return;
    try {
      await desativar.mutateAsync({ id: aDesativar.id });
      await recarregar();
      toast.success(`"${aDesativar.nome}" desativado.`);
    } catch (erro) {
      toast.error(
        erro instanceof ApiError ? erro.message : 'Não foi possível desativar o fármaco.',
      );
    } finally {
      setADesativar(null);
    }
  }

  async function reativar(farmaco: CatalogoFarmacoResponse) {
    try {
      await atualizar.mutateAsync({ id: farmaco.id, data: { ativo: true } });
      await recarregar();
      toast.success(`"${farmaco.nome}" reativado.`);
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível reativar o fármaco.');
    }
  }

  return (
    <div className="space-y-6">
      {/*
        ⚠️ A contagem fica NO rótulo da aba, não escondida atrás do clique. É a
        única coisa que avisa o admin de que há trabalho esperando — e sem esse
        aviso o catálogo global enche de "trilostano", "Trilostano" e
        "trilostan" com a tela parecendo em ordem.
      */}
      <div className="flex items-center gap-1 border-b border-brand/10">
        <button
          type="button"
          onClick={() => setAba('catalogo')}
          aria-current={aba === 'catalogo' ? 'page' : undefined}
          className={
            aba === 'catalogo'
              ? 'border-b-2 border-brand px-4 py-2 text-sm font-semibold text-brand'
              : 'border-b-2 border-transparent px-4 py-2 text-sm text-muted-foreground hover:text-foreground'
          }
        >
          Catálogo
        </button>

        <button
          type="button"
          onClick={() => setAba('sugestoes')}
          aria-current={aba === 'sugestoes' ? 'page' : undefined}
          className={
            aba === 'sugestoes'
              ? 'flex items-center gap-2 border-b-2 border-brand px-4 py-2 text-sm font-semibold text-brand'
              : 'flex items-center gap-2 border-b-2 border-transparent px-4 py-2 text-sm text-muted-foreground hover:text-foreground'
          }
        >
          Sugestões do clínico
          {totalDeSugestoes > 0 && (
            <Badge variant="outline" className="text-amber-600 border-amber-600/40">
              {totalDeSugestoes}
            </Badge>
          )}
        </button>
      </div>

      {aba === 'sugestoes' ? (
        <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
            <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">
              Sugestões do clínico
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Fármacos que um veterinário digitou ao montar um tratamento e que ainda não passaram
              por curadoria. Eles já são filtráveis na Análise Comparativa — o que falta é decidir
              se cada um é uma <strong>duplicata</strong> (funda no item que fica) ou um{' '}
              <strong>princípio a mais</strong> (complete sinônimos e classe, e promova).
            </p>
          </div>

          <SugestoesDoClinico />
        </div>
      ) : (
        <>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div className="flex flex-col sm:flex-row gap-3 flex-1">
              <Input
                value={busca}
                onChange={(e) => aoFiltrar(() => setBusca(e.target.value))}
                placeholder="Buscar por princípio ativo, sinônimo ou marca..."
                aria-label="Buscar fármaco"
                className="sm:max-w-xs"
              />

              {classes && classes.itens.length > 0 && (
                <Select value={classe} onValueChange={(v) => aoFiltrar(() => setClasse(v))}>
                  <SelectTrigger className="sm:w-56" aria-label="Filtrar por classe terapêutica">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TODOS}>Todas as classes</SelectItem>
                    {classes.itens.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Select value={situacao} onValueChange={(v) => aoFiltrar(() => setSituacao(v))}>
                <SelectTrigger className="sm:w-40" aria-label="Filtrar por status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TODOS}>Todos os status</SelectItem>
                  <SelectItem value="true">Ativos</SelectItem>
                  <SelectItem value="false">Desativados</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {podeCriar && (
              <Button
                onClick={() => {
                  setEmEdicao(undefined);
                  setFormAberto(true);
                }}
              >
                <Plus className="w-4 h-4" />
                Novo fármaco
              </Button>
            )}
          </div>

          <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
              <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">
                Catálogo de fármacos
              </h3>
            </div>

            {isPending ? (
              <div className="p-6 space-y-3">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-10 rounded-md" />
                ))}
              </div>
            ) : isError || !data ? (
              <EstadoErro aoTentarDeNovo={() => void refetch()} />
            ) : data.itens.length === 0 ? (
              <EstadoVazio
                icone={Pill}
                titulo="Nenhum fármaco encontrado"
                descricao={
                  busca
                    ? 'Tente outro termo — a busca também olha sinônimos e nome comercial.'
                    : 'Cadastre o primeiro princípio ativo.'
                }
              />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fármaco</TableHead>
                        <TableHead>Classe terapêutica</TableHead>
                        <TableHead>Apresentações</TableHead>
                        <TableHead>Em uso</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-20">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.itens.map((farmaco) => (
                        <TableRow key={farmaco.id}>
                          <TableCell>
                            <Link
                              to={rotaDoFarmacoDoCatalogo(farmaco.id)}
                              className="font-medium text-foreground hover:text-brand transition-colors"
                            >
                              {farmaco.nome}
                            </Link>

                            {farmaco.sinonimos.length > 0 && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {farmaco.sinonimos.join(' · ')}
                              </p>
                            )}

                            {/* Item fundido: sem a seta, a linha desativada parece
                            simplesmente abandonada, e ninguém descobre para
                            onde a leitura foi. */}
                            {farmaco.substituidoPorNome && (
                              <p className="text-xs text-amber-600 mt-0.5">
                                → fundido em {farmaco.substituidoPorNome}
                              </p>
                            )}
                          </TableCell>

                          <TableCell className="text-muted-foreground">
                            {farmaco.classeTerapeutica ?? '—'}
                          </TableCell>

                          <TableCell className="text-muted-foreground tabular-nums">
                            {farmaco.totalApresentacoes}
                          </TableCell>

                          {/* ⚠️ `totalEmUso` não é enfeite: é quantos tratamentos
                          apontam para cá. É o número que explica por que este
                          catálogo desativa em vez de apagar. */}
                          <TableCell className="text-muted-foreground tabular-nums">
                            <span title={`${farmaco.totalEmUso} tratamento(s) usam este fármaco`}>
                              {farmaco.totalEmUso}
                            </span>
                          </TableCell>

                          <TableCell>
                            {farmaco.ativo ? (
                              <Badge
                                variant="outline"
                                className="text-emerald-600 border-emerald-600/40"
                              >
                                Ativo
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className="text-destructive border-destructive/40"
                              >
                                Desativado
                              </Badge>
                            )}
                          </TableCell>

                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label={`Ações de ${farmaco.nome}`}
                                >
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {podeEditar && (
                                  <>
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setEmEdicao(farmaco);
                                        setFormAberto(true);
                                      }}
                                    >
                                      <Pencil className="w-4 h-4" />
                                      Editar
                                    </DropdownMenuItem>

                                    {/* Item já fundido não funde de novo: o servidor
                                    recusa o destino já fundido, e oferecer a
                                    ação seria oferecer um 409. */}
                                    {farmaco.ativo && !farmaco.substituidoPorId && (
                                      <DropdownMenuItem onClick={() => setAFundir(farmaco)}>
                                        <ArrowRightLeft className="w-4 h-4" />
                                        Fundir em outro
                                      </DropdownMenuItem>
                                    )}
                                  </>
                                )}

                                {podeExcluir &&
                                  (farmaco.ativo ? (
                                    <DropdownMenuItem
                                      onClick={() => setADesativar(farmaco)}
                                      className="text-destructive focus:text-destructive"
                                    >
                                      <PowerOff className="w-4 h-4" />
                                      Desativar
                                    </DropdownMenuItem>
                                  ) : (
                                    podeEditar && (
                                      <DropdownMenuItem onClick={() => void reativar(farmaco)}>
                                        <Power className="w-4 h-4" />
                                        Reativar
                                      </DropdownMenuItem>
                                    )
                                  ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <Paginacao
                  pagina={data.pagina}
                  totalPaginas={data.totalPaginas}
                  total={data.total}
                  porPagina={data.porPagina}
                  aoMudarPagina={setPagina}
                />
              </>
            )}
          </div>
        </>
      )}

      <FarmacoFormDialogo
        aberto={formAberto}
        aoFechar={() => setFormAberto(false)}
        {...(emEdicao ? { farmaco: emEdicao } : {})}
      />

      {aFundir && (
        <FundirFarmacoDialogo aberto aoFechar={() => setAFundir(null)} farmaco={aFundir} />
      )}

      {/* ⚠️ O aviso cita o número de tratamentos de propósito: é ele que separa
          "desativar" de "apagar". Nada some do histórico. */}
      <AlertDialog open={Boolean(aDesativar)} onOpenChange={(v) => !v && setADesativar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar &quot;{aDesativar?.nome}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              {aDesativar?.totalEmUso === 0 ? (
                <>
                  Nenhum tratamento usa este fármaco hoje. Ele sai dos seletores e pode ser
                  reativado a qualquer momento.
                </>
              ) : (
                <>
                  <strong>
                    {aDesativar?.totalEmUso} tratamentos usam este fármaco e continuarão
                    mostrando-o.
                  </strong>{' '}
                  Ele apenas sai dos seletores para novos registros. Se a intenção é resolver uma
                  duplicata, use “Fundir em outro” — desativar sem fundir deixa essa coorte fora da
                  Análise Comparativa.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmarDesativacao()}>
              Desativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
